import { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress,
  InputAdornment, TextField,
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import ErrorIcon        from '@mui/icons-material/Error';
import SearchIcon       from '@mui/icons-material/Search';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon  from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon   from '@mui/icons-material/UnfoldMore';
import { getCredentialedScan } from '../api/tenable';

const THRESHOLD = 98;

function StatCard({ label, value, sub, color }) {
  return (
    <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, p: '18px 24px', flex: 1, minWidth: 130 }}>
      <Typography sx={{ color, fontSize: 36, fontWeight: 700, lineHeight: 1, fontFamily: 'monospace' }}>
        {value}
      </Typography>
      <Typography sx={{ color: '#ffffff', fontSize: 13, fontWeight: 600, mt: 0.5 }}>{label}</Typography>
      {sub && <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.25 }}>{sub}</Typography>}
    </Paper>
  );
}

const COLS = [
  { key: 'ip',           label: 'IP Address',  width: 140 },
  { key: 'hostname',     label: 'Device Name', width: 200 },
  { key: 'os',           label: 'OS',          width: 180 },
  { key: 'plugin_id',    label: 'Plugin',      width: 220 },
  { key: 'reason',       label: 'Scan Result', width: 'flex' },
];

function ColHeader({ col, sortBy, sortDir, onSort }) {
  const active = sortBy === col.key;
  const Icon = active ? (sortDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon) : UnfoldMoreIcon;
  return (
    <Box
      onClick={() => onSort(col.key)}
      sx={{
        width: col.width === 'flex' ? undefined : col.width,
        flex: col.width === 'flex' ? 1 : undefined,
        display: 'flex', alignItems: 'center', gap: 0.5,
        cursor: 'pointer', userSelect: 'none',
        color: active ? '#00bcd4' : '#7a8fa6',
        fontSize: 11, fontWeight: active ? 700 : 500,
        textTransform: 'uppercase', letterSpacing: 0.5,
        '&:hover': { color: '#00bcd4' },
      }}
    >
      {col.label}
      <Icon sx={{ fontSize: 13 }} />
    </Box>
  );
}

function HostRow({ h, view }) {
  const failed = view === 'failed';
  const color  = failed ? '#f44336' : '#4caf50';
  const Icon   = failed ? ErrorIcon : CheckCircleIcon;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0,
      borderBottom: '1px solid #1a2535',
      '&:last-child': { borderBottom: 'none' },
      '&:hover': { backgroundColor: '#0d1825' },
      transition: 'background 0.1s',
    }}>
      {/* Status icon + IP */}
      <Box sx={{ width: 140, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, p: '12px 8px 12px 16px' }}>
        <Icon sx={{ color, fontSize: 15, flexShrink: 0 }} />
        <Typography sx={{ color: '#c0cfe0', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>
          {h.ip || '—'}
        </Typography>
      </Box>

      {/* Hostname */}
      <Box sx={{ width: 200, flexShrink: 0, p: '12px 8px' }}>
        <Typography sx={{ color: '#a0b4c8', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {h.hostname || '—'}
        </Typography>
      </Box>

      {/* OS */}
      <Box sx={{ width: 180, flexShrink: 0, p: '12px 8px' }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {h.os || '—'}
        </Typography>
      </Box>

      {/* Plugin */}
      <Box sx={{ width: 220, flexShrink: 0, p: '12px 8px' }}>
        <Chip
          label={`${h.plugin_id} — ${h.plugin_title}`}
          size="small"
          sx={{
            backgroundColor: `${color}15`,
            color,
            fontSize: 10,
            height: 20,
            maxWidth: 204,
            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          }}
        />
      </Box>

      {/* Scan result */}
      <Box sx={{ flex: 1, minWidth: 0, p: '12px 16px 12px 8px' }}>
        <Typography sx={{
          color: h.reason ? (failed ? '#f47a6a' : '#7a8fa6') : '#4a5a70',
          fontSize: 12,
          fontStyle: h.reason ? 'normal' : 'italic',
        }}>
          {h.reason || (failed ? 'No additional output' : 'Credentialed checks successful')}
        </Typography>
        {h.last_seen && (
          <Typography sx={{ color: '#4a5a70', fontSize: 11, mt: 0.25 }}>
            Last seen: {new Date(h.last_seen).toLocaleDateString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function CredentialedScan({ systemId, onBack }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [view,    setView]    = useState('failed');
  const [search,  setSearch]  = useState('');
  const [sortBy,  setSortBy]  = useState('ip');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (!systemId) return;
    getCredentialedScan(systemId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [systemId]);

  const { hosts = [], coverage_rate = 0, x_count = 0, y_count = 0, failure_rate = 0 } = data || {};

  const color = coverage_rate >= THRESHOLD ? '#4caf50' : coverage_rate >= 83 ? '#ff9800' : '#f44336';

  const failed  = useMemo(() => hosts.filter(h => h.status !== 'good'), [hosts]);
  const success = useMemo(() => hosts.filter(h => h.status === 'good'), [hosts]);

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    const list = view === 'failed' ? failed : success;
    const q = search.toLowerCase();
    const filtered = q
      ? list.filter(h =>
          (h.ip           || '').includes(q) ||
          (h.hostname     || '').toLowerCase().includes(q) ||
          (h.os           || '').toLowerCase().includes(q) ||
          (h.plugin_id    || '').includes(q) ||
          (h.plugin_title || '').toLowerCase().includes(q) ||
          (h.reason       || '').toLowerCase().includes(q)
        )
      : list;

    return [...filtered].sort((a, b) => {
      const av = (a[sortBy] || '').toString().toLowerCase();
      const bv = (b[sortBy] || '').toString().toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [view, failed, success, search, sortBy, sortDir]);

  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#00bcd4' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', p: 3 }}>

      {/* Breadcrumb */}
      <Box onClick={onBack} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2.5, cursor: 'pointer', color: '#7a8fa6', '&:hover': { color: '#00bcd4' }, width: 'fit-content' }}>
        <ArrowBackIcon sx={{ fontSize: 15 }} />
        <Typography sx={{ fontSize: 12 }}>Back to Vulnerabilities</Typography>
      </Box>

      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography sx={{ color: '#ffffff', fontSize: 22, fontWeight: 700 }}>
          Plugin 19506 — Credentialed Scan
        </Typography>
        <Chip
          label={coverage_rate >= THRESHOLD ? 'MEETS CORA THRESHOLD' : 'BELOW THRESHOLD'}
          size="small"
          sx={{ backgroundColor: `${color}22`, color, fontWeight: 700, fontSize: 11 }}
        />
      </Box>

      {/* Stat cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Coverage Rate"  value={`${coverage_rate}%`}           color={color}                                          sub={`CORA threshold ≥${THRESHOLD}%`} />
        <StatCard label="Credentialed"   value={x_count}                        color="#4caf50"                                        sub="Passed credentialed check" />
        <StatCard label="Failed"         value={y_count}                        color="#f44336"                                        sub="Failed credentialed check" />
        <StatCard label="Failure Rate"   value={`${failure_rate}%`}             color={failure_rate <= 2 ? '#4caf50' : '#f44336'}      sub="Target ≤2%" />
        <StatCard label="Total Scanned"  value={hosts.length}                   color="#7a8fa6"                                        sub="Hosts with plugin 19506" />
      </Box>

      {/* Filter + Search row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {/* Toggle */}
        <Box sx={{ display: 'flex', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
          {[
            { key: 'failed',  label: 'Credentialed Failed',  color: '#f44336', count: failed.length },
            { key: 'success', label: 'Credentialed Success', color: '#4caf50', count: success.length },
          ].map(opt => {
            const active = view === opt.key;
            return (
              <Box key={opt.key} onClick={() => { setView(opt.key); setSearch(''); setSortBy('ip'); setSortDir('asc'); }} sx={{
                px: 2.5, py: 1.25, cursor: 'pointer',
                backgroundColor: active ? `${opt.color}18` : 'transparent',
                borderRight: opt.key === 'failed' ? '1px solid #1e3a5f' : 'none',
                display: 'flex', alignItems: 'center', gap: 1,
                '&:hover': { backgroundColor: `${opt.color}10` },
                transition: 'background 0.15s',
              }}>
                <Typography sx={{ color: active ? opt.color : '#7a8fa6', fontSize: 13, fontWeight: active ? 700 : 400 }}>
                  {opt.label}
                </Typography>
                <Chip label={opt.count} size="small" sx={{
                  backgroundColor: active ? `${opt.color}22` : '#1e3a5f',
                  color: active ? opt.color : '#7a8fa6',
                  fontSize: 10, height: 18, fontWeight: 700,
                }} />
              </Box>
            );
          })}
        </Box>

        {/* Search */}
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by IP, device, OS, or plugin…"
          size="small"
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#111827', color: '#c0cfe0', fontSize: 13,
              '& fieldset': { borderColor: '#1e3a5f' },
              '&:hover fieldset': { borderColor: '#2a4a6f' },
              '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
            },
            '& input::placeholder': { color: '#4a5a70' },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#4a5a70', fontSize: 16 }} /></InputAdornment>,
          }}
        />
      </Box>

      {/* Table */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>

        {/* Column headers */}
        <Box sx={{ display: 'flex', p: '10px 0', borderBottom: '2px solid #1e3a5f', backgroundColor: '#0d1825' }}>
          {COLS.map(col => (
            <Box key={col.key} sx={{ width: col.width === 'flex' ? undefined : col.width, flex: col.width === 'flex' ? 1 : undefined, px: col.key === 'ip' ? '16px' : '8px' }}>
              <ColHeader col={col} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </Box>
          ))}
        </Box>

        {/* Rows */}
        {sorted.length === 0 ? (
          <Typography sx={{ color: '#4a5a70', textAlign: 'center', p: 5, fontStyle: 'italic' }}>
            {search ? 'No hosts match your search.' : `No hosts in ${view === 'failed' ? 'failed' : 'success'} category.`}
          </Typography>
        ) : (
          sorted.map((h, i) => <HostRow key={i} h={h} view={view} />)
        )}

        {/* Footer count */}
        {sorted.length > 0 && (
          <Box sx={{ p: '8px 16px', borderTop: '1px solid #1a2535' }}>
            <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>
              {sorted.length} host{sorted.length !== 1 ? 's' : ''}
              {search ? ` matching "${search}"` : ''}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
